import Vue from 'vue';
import axios, { AxiosResponse, AxiosError } from 'axios';
import { Component } from 'vue-property-decorator';
import moment from 'moment';
import { EntityType } from '@/models/EntityType';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { postKeepaliveRelease } from '@/util/lockRelease';
import { isHeldByAnotherTab } from '@/util/sharedLocks';
import { postToWorkerPort } from '@/util/workerPort';
import { hasValidAuthHeader } from '@/util/authShared';

export interface Lock {
    refreshURL: string;
    intervalNumber: NodeJS.Timeout;
    lockedBy: string;
    locked: Date;
}

/** The Authorization header the app's interceptors keep current, if a real token is set. */
function currentAuthHeader(): string | null {
    const auth = axios.defaults.headers?.Authorization;
    return hasValidAuthHeader(auth) ? auth as string : null;
}

/**
 * Absolute release URL for a lock path. Deliberately axios's own resolver, so the keepalive
 * release lands on exactly the URL the acquire and heartbeat used.
 */
function absoluteLockUrl(lockPath: string): string {
    return axios.getUri({ url: lockPath });
}

@Component
export class LockHandler extends Vue {
    created(): void {
        console.log("%c Created LockHandler", "color: purple");
        window.addEventListener('focus', this.activatedEvent);
        window.addEventListener('blur', this.deactivatedEvent);
        window.addEventListener('pagehide', this.pagehideEvent);
    }
    beforeDestroy(): void {
        console.log('%c Destroying LockHandler', 'color: magenta');
        window.removeEventListener('focus', this.activatedEvent);
        window.removeEventListener('blur', this.deactivatedEvent);
        window.removeEventListener('pagehide', this.pagehideEvent);
        this.clearAllLocks();
    }

    /**
     * The document is going away (tab close, navigation off-origin, bfcache entry) and Vue teardown
     * won't run, so release here with a keepalive fetch — axios requests are aborted on unload.
     *
     * We do this ourselves rather than delegate to the shared worker: postMessage to a worker that
     * has already been torn down is a spec'd silent no-op, not a throw, so there is no way to know
     * from here whether a handoff landed. Outside Chromium the worker dies with the last tab, which
     * would make that the common case. The worker instead keeps us told which locks a second tab
     * also holds, and we skip those — ClearLock is scoped to (record, user), so releasing one would
     * take it from the tab still using it. `tabClosing` is still sent so the worker drops this port
     * promptly, but nothing here depends on it arriving.
     */
    pagehideEvent(event: PageTransitionEvent): void {
        // `persisted` means the document is entering the bfcache, not actually closing — it can be
        // restored by a Back/Forward navigation at any moment, so the tab is still open and must
        // keep its locks. An evicted-and-never-restored bfcache entry just falls back to the
        // server-side lock expiry, same as any other unreachable-tab case.
        if (event.persisted) return;

        // The mixin is on dozens of components, most holding nothing.
        if (this.locks.length === 0) return;

        const auth = currentAuthHeader();
        // URLs this tab actually released itself, so the worker knows not to replay them — anything
        // it skipped (believing a sibling tab still held it) is left for the worker to reconcile,
        // since two tabs on the same record can both skip in the same tick if they're closing
        // together (see forgetPortReplayingUnreleased).
        const released: string[] = [];

        for (const lock of this.locks) {
            const url = absoluteLockUrl(lock.refreshURL);
            if (isHeldByAnotherTab(url)) continue;
            postKeepaliveRelease(url, auth);
            released.push(url);
        }

        postToWorkerPort('tabClosing', { auth, released });
    }

    /** Mirrors lock lifecycle to the worker so it can replay releases if this tab dies without unloading. */
    private postLockToWorker(type: 'lockAcquired' | 'lockReleased', url: string): void {
        postToWorkerPort(type, { url: absoluteLockUrl(url), auth: currentAuthHeader() });
    }

    activatedEvent(): void {
        this.hasFocus = true;
    }

    deactivatedEvent(): void {
        this.hasFocus = false;
    }
    private locks: Lock[] = [];
    private hasFocus = true;
    // Bumped every time all locks are abandoned — via clearAllLocks (route change)
    // or beforeDestroy, both of which call clearAllLocks. An in-flight addLockWithURL
    // captures the generation it started in and releases instead of registering if the
    // generation has moved on by the time it resolves.
    private lockGeneration = 0;
    // Sequence for setExclusiveLock. Each call takes the next value; a call whose value
    // has been superseded by a later one by the time its async work resolves backs out
    // (releasing what it acquired), so only the most recently requested record stays locked.
    private exclusiveLockSeq = 0;

    public async clearAllLocks(): Promise<void> {
        // Invalidate any lock acquisition currently in-flight: when its POST resolves
        // it will see the bumped generation and release itself instead of registering
        // an interval this clear can't see (the lock isn't in this.locks yet).
        this.lockGeneration++;

        // Nothing to clear
        if (this.locks.length === 0) {
            console.log('No locks to clear');
            return;
        }

        console.log('%c Clearing all locks', 'color: magenta; font-weight: bold');

        // Detach the current locks synchronously before awaiting the release POSTs. A lock
        // registered by an in-flight addLockWithURL while the awaits below run then lands in
        // the fresh this.locks array instead of being clobbered into an untracked orphan.
        // The detach also makes concurrent calls safe without serializing them: each call
        // releases exactly the locks it detached, so a call overlapping a slow clear still
        // reaps anything registered since — no lock is ever skipped or released twice.
        const locksToClear = this.locks;
        this.locks = [];

        const unlockPromises = locksToClear.map(lock => {
            clearInterval(lock.intervalNumber);
            return axios.post(lock.refreshURL, true)
                .then(() => {
                    console.log(`Lock released: ${lock.refreshURL}`);
                    this.postLockToWorker('lockReleased', lock.refreshURL);
                })
                .catch(error => {
                    console.error(`Failed to release lock at ${lock.refreshURL}:`, error);
                });
        });

        // Wait for all unlocks to complete
        await Promise.all(unlockPromises);
    }

    /**
     * Acquire `url` as this component's sole lock, first releasing any other lock it holds
     * — whether already registered or still in flight. Use this for single-record edit
     * modals (on open, or when switching records) instead of storing a lock field and
     * releasing it by URL on close: that pattern races the close against the acquisition
     * POST, so a fast close leaves the heartbeat renewing the lease forever.
     *
     * Latest call wins: if another setExclusiveLock starts before this one finishes, this
     * one releases whatever it acquired and resolves to null. A rejected acquisition (e.g.
     * 418 locked-by-another-user) is re-thrown so callers can surface the locked message.
     */
    public async setExclusiveLock(url: string, refreshInterval: number): Promise<Lock | null> {
        const seq = ++this.exclusiveLockSeq;
        await this.clearAllLocks();
        if (seq !== this.exclusiveLockSeq) return null;
        let lock: Lock;
        try {
            lock = await this.addLockWithURL(url, refreshInterval);
        } catch (err) {
            // A server response (e.g. 418 locked-by-another-user) is a real outcome the
            // caller must surface, so re-throw it. Any other rejection means the acquisition
            // was invalidated in flight — the modal closed, or a newer setExclusiveLock bumped
            // the generation — in which case addLockWithURL has already released it. Treat that
            // as a benign no-op rather than letting it reach the caller as an error toast.
            if ((err as AxiosError)?.response) throw err;
            return null;
        }
        if (seq !== this.exclusiveLockSeq) {
            // The superseding call's clearAllLocks usually already released this lock; only
            // back it out ourselves if it's still registered, avoiding a duplicate release POST.
            if (this.locks.some(l => l.refreshURL === url)) {
                this.releaseLockAtURL(url);
            }
            return null;
        }
        return lock;
    }

    /** The registered lock at `url`, if any. Unlike a caller's stored copy, this reflects a heartbeat 418 release. */
    public getLockAtURL(url: string): Lock | undefined {
        return this.locks.find(l => l.refreshURL === url);
    }

    /**
     * `ignoreFocus` exempts only this initial acquire from the focus gate — for registering a lock the server
     * already holds for this user, which must not be skipped because the window blurred. Renewals stay gated.
     */
    public addLockWithURL(url: string, refreshInterval: number, ignoreFocus = false): Promise<Lock> {
        const generation = this.lockGeneration;
        return this.refreshAtURL(url, ignoreFocus)
            .then(result => {
                if (this.lockGeneration !== generation) {
                    // All locks were abandoned — via a route change or via beforeDestroy —
                    // while this lock POST was in-flight, so this lock now has no owner.
                    // Release it immediately so it neither stays open on the server nor
                    // spawns an orphaned refresh interval. releaseLockAtURL also logs
                    // failures; its locks.filter/clearInterval are no-ops here since this
                    // lock was never pushed.
                    this.releaseLockAtURL(url);
                    throw new Error(`Lock at ${url} was invalidated before registration — released immediately`);
                }
                const intervalNumber = setInterval(
                    () => {
                        this.refreshAtURL(url)
                            // Re-mirroring on every renewal — rather than only on acquire — is what
                            // makes the worker's view self-correcting: it recovers from a bfcache
                            // restore, a dropped message, or a worker restart without a special case.
                            // Guarded because clearInterval can't cancel a refresh already in flight,
                            // and re-adding a lock the page just released would strand it forever.
                            .then(() => {
                                if (this.locks.some(l => l.refreshURL === url)) {
                                    this.postLockToWorker('lockAcquired', url);
                                }
                            })
                            .catch(error => {
                                if (error.message == `We don't have focus`) {
                                    console.warn(`Window doesn't have focus, not refreshing the lock.`);
                                }
                                if (error.response && error.response.status == 418) {
                                    const lockURL = error.config.url;
                                    console.warn(`Had to release lock at ${lockURL}`);
                                    this.releaseLockAtURL(error.config.url);
                                }
                            });
                    },
                    refreshInterval);
                const lock: Lock = {
                    refreshURL: url,
                    intervalNumber: intervalNumber,
                    lockedBy: result.data.lockedBy,
                    locked: result.data.locked
                };
                this.locks.push(lock);
                this.postLockToWorker('lockAcquired', url);
                return lock;
            })
            .catch(error => {
                throw error;
            });
    }

    async refreshAtURL(url: string, ignoreFocus = false): Promise<AxiosResponse<any>> {
        if (!ignoreFocus && !this.hasFocus) {
            throw new Error(`We don't have focus`);
        }
        return axios.post(url, false)
            .then(response => {
                return response;
            })
            .catch(error => {
                throw error;
            });
    }

    releaseLockAtURL(url: string): void {
        this.locks = this.locks.filter((value: Lock) => {
            if (value.refreshURL == url) {
                clearInterval(value.intervalNumber);
            }
            return value.refreshURL != url;
        });

        // Release lock with proper error handling
        axios.post(url, true)
            .then(() => {
                console.log(`Lock released: ${url}`);
                // Untrack in the worker only once the server has actually let go. A failed release
                // leaves the lock mirrored, so the worker retries it when this tab goes away.
                this.postLockToWorker('lockReleased', url);
            })
            .catch((error) => {
                console.error(`Failed to release lock at ${url}:`, error);
            });
    }

    async showLockedMessage(entityType: EntityType, entityId: string, entityRecordName: string, lockData: any): Promise<void> {
        try {
            const lockedBy = lockData.lockedBy;
            const expires = lockData.expires;
            const localExpiration = moment.utc(expires).local().format("L LT");
            const entityName = EntityType[entityType];
            const requestRelease = await this.$root.$bvModal.msgBoxConfirm(`This ${entityName} is locked by ${lockedBy} until ${localExpiration}.`,
                {
                    title: `${entityName} Locked`,
                    cancelTitle: 'Ok',
                    okTitle: 'Request Release from User',
                    noFade: true,
                    centered: true
                });

            if (!requestRelease) return;

            try {
                await Axios.post("/notification/request-lock-release", {
                    entityType,
                    entityId,
                    entityName: entityRecordName,
                    username: lockedBy
                });
                this.$notification(NotificationOptions.success(`Sent lock release request to ${lockedBy}`));
            } catch (error) {
                console.warn(error);
                this.$notification(NotificationOptions.error("Error sending lock release request"));
            }

        } catch (err) {
            console.warn(err);
        }
    }
}
